import React, { useEffect, useState } from 'react';
import {
    AreaChart,
    Area,
    BarChart,
    Bar,
    CartesianGrid,
    XAxis,
    YAxis,
    Tooltip,
    Legend,
    ResponsiveContainer
} from 'recharts';
import { TrendingUp } from 'lucide-react';
import { ChartCard, EmptyState, COLORS, formatRevenue } from './common.jsx';
import DualRangeSlider from '../../common/DualRangeSlider';

const TrendsTab = ({ data, onGameClick }) => {
    const {
        temporal = { monthly: [] },
        topTrending = [],
        trendingGenres = [],
        regionalGrowth = []
    } = data || {};
    const [range, setRange] = useState([0, Math.max((temporal.monthly || []).length - 1, 0)]);

    useEffect(() => {
        setRange([0, Math.max((temporal.monthly || []).length - 1, 0)]);
    }, [temporal.monthly?.length]);

    const monthlyData = temporal.monthly || [];
    const hasMonthlyData = monthlyData.length > 0;
    const clampedStart = Math.min(range[0], Math.max(monthlyData.length - 1, 0));
    const clampedEnd = Math.max(clampedStart, Math.min(range[1], Math.max(monthlyData.length - 1, 0)));
    const filteredMonthly = hasMonthlyData ? monthlyData.slice(clampedStart, clampedEnd + 1) : [];
    const startLabel = hasMonthlyData ? (filteredMonthly[0]?.period || filteredMonthly[0]?.month || '—') : '—';
    const endLabel = hasMonthlyData ? (filteredMonthly[filteredMonthly.length - 1]?.period || filteredMonthly[filteredMonthly.length - 1]?.month || '—') : '—';

    return (
        <div className="space-y-6">
            <ChartCard title="Market Trends Over Time" subtitle="Monthly release and revenue patterns" fullWidth>
                {hasMonthlyData ? (
                    <>
                        <div className="flex items-center justify-end mb-2 text-xs text-gray-600 space-x-3">
                            <div className="flex items-center space-x-2">
                                <span className="font-semibold">Range:</span>
                                <span>{startLabel}</span>
                                <span className="text-gray-400">→</span>
                                <span>{endLabel}</span>
                            </div>
                            <div className="flex items-center space-x-2 w-64">
                                <DualRangeSlider
                                    min={0}
                                    max={monthlyData.length - 1}
                                    value={[clampedStart, clampedEnd]}
                                    onChange={(newRange) => setRange(newRange)}
                                    formatLabel={(index) => {
                                        if (index < 0 || index >= monthlyData.length) return '—';
                                        return monthlyData[index]?.period || monthlyData[index]?.month || '—';
                                    }}
                                />
                            </div>
                        </div>
                        <ResponsiveContainer width="100%" height={420}>
                            <AreaChart
                                data={filteredMonthly}
                                margin={{ top: 10, right: 24, left: 16, bottom: 24 }}
                            >
                                <defs>
                                    <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="5%" stopColor={COLORS.primary} stopOpacity={0.8} />
                                        <stop offset="95%" stopColor={COLORS.primary} stopOpacity={0} />
                                    </linearGradient>
                                    <linearGradient id="colorGames" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="5%" stopColor={COLORS.success} stopOpacity={0.8} />
                                        <stop offset="95%" stopColor={COLORS.success} stopOpacity={0} />
                                    </linearGradient>
                                </defs>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis dataKey="period" />
                                <YAxis yAxisId="left" tickFormatter={(value) => formatRevenue(value)} />
                                <YAxis yAxisId="right" orientation="right" />
                                <Tooltip formatter={(value, name) => {
                                    if (name === 'Revenue' || name === 'Total Revenue') {
                                        return formatRevenue(value);
                                    }
                                    return value.toLocaleString();
                                }} />
                                <Legend />
                                <Area yAxisId="left" type="monotone" dataKey="totalRevenue" stroke={COLORS.primary} fillOpacity={1} fill="url(#colorRevenue)" name="Revenue" />
                                <Area yAxisId="right" type="monotone" dataKey="gameCount" stroke={COLORS.success} fillOpacity={1} fill="url(#colorGames)" name="Games Released" />
                            </AreaChart>
                        </ResponsiveContainer>
                    </>
                ) : (
                    <EmptyState message="No temporal trend data available" />
                )}
            </ChartCard>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <ChartCard title="🔥 Hottest Games" subtitle="Top 10 trending titles right now">
                    {topTrending && topTrending.length > 0 ? (
                        <div className="space-y-3 max-h-96 overflow-y-auto">
                            {topTrending.slice(0, 10).map((game, index) => (
                                <button
                                    key={game.steamId || index}
                                    className="flex items-center justify-between w-full text-left p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                                    onClick={() => onGameClick?.(game)}
                                >
                                    <div className="flex items-center space-x-3 flex-1">
                                        <span className="text-2xl font-bold text-gray-300">#{index + 1}</span>
                                        <div className="flex-1">
                                            <h4 className="font-semibold text-gray-900 truncate">{game.name || 'Unknown'}</h4>
                                            <p className="text-sm text-gray-600">
                                                {(game.genres || []).slice(0, 2).join(', ') || '—'} • ${game.price ?? '—'}
                                            </p>
                                        </div>
                                    </div>
                                    <div className="text-right">
                                        <div className="flex items-center space-x-2">
                                            <TrendingUp className="h-4 w-4 text-green-500" />
                                            <span className="font-bold text-green-600">{Math.round(game.trendingScore || 0)}</span>
                                        </div>
                                        <p className="text-xs text-gray-500">{game.revenue != null ? formatRevenue(game.revenue) : '—'}</p>
                                    </div>
                                </button>
                            ))}
                        </div>
                    ) : (
                        <EmptyState message="No trending games available" />
                    )}
                </ChartCard>

                <ChartCard title="🌍 Regional Performance" subtitle="Top markets by revenue">
                    {regionalGrowth && regionalGrowth.length > 0 ? (
                        <ResponsiveContainer width="100%" height={350}>
                            <BarChart data={regionalGrowth} layout="vertical">
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis type="number" tickFormatter={(value) => formatRevenue(value)} />
                                <YAxis dataKey="language" type="category" width={60} />
                                <Tooltip formatter={(value, name) => {
                                    if (name === 'Revenue' || name === 'Total Revenue') {
                                        return formatRevenue(value);
                                    }
                                    return value.toLocaleString();
                                }} />
                                <Bar dataKey="avgRevenue" fill={COLORS.teal} />
                            </BarChart>
                        </ResponsiveContainer>
                    ) : (
                        <EmptyState message="No regional data available" />
                    )}
                </ChartCard>
            </div>
        </div>
    );
};

export default TrendsTab;
